import React, { useState, useMemo } from "react";

const getDomain = (url) => {
  try { return new URL(url).hostname.replace(/^www\./, ""); }
  catch { return ""; }
};

const truncate = (s, n) => {
  if (!s) return "";
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
};

// Renders a collapsible "Sources" strip at the bottom of an insight section.
// Pulls items from `serpLinks` for the given SERP label keys and groups them by
// domain so 8 different linkedin.com URLs no longer look like 8 identical chips.
//
// Default state: collapsed — single summary line ("Sources · 12 · linkedin.com, ...").
// Expanded: domain-grouped sub-sections, each with its own collapse toggle.
// Inside each domain group: page TITLE (truncated), not just the domain — so
// same-domain entries are actually distinguishable.
const SectionSources = ({ serpLinks, labels = [], max = 30 }) => {
  const [open, setOpen] = useState(false);
  const [openDomains, setOpenDomains] = useState({});

  // Aggregate + dedupe + group-by-domain
  const grouped = useMemo(() => {
    if (!serpLinks || !labels.length) return { domains: [], totalCount: 0 };
    const seenLinks = new Set();
    const byDomain = new Map();
    let totalCount = 0;
    for (const key of labels) {
      const bucket = serpLinks[key];
      if (!Array.isArray(bucket)) continue;
      for (const it of bucket) {
        const link = it && it.link;
        if (!link || seenLinks.has(link)) continue;
        seenLinks.add(link);
        const domain = getDomain(link) || "other";
        if (!byDomain.has(domain)) byDomain.set(domain, []);
        if (byDomain.get(domain).length < max) {
          byDomain.get(domain).push(it);
          totalCount += 1;
        }
      }
    }
    const domains = Array.from(byDomain.entries())
      .map(([domain, items]) => ({ domain, items }))
      .sort((a, b) => b.items.length - a.items.length);
    return { domains, totalCount };
  }, [serpLinks, labels, max]);

  if (!grouped.domains.length) return null;

  const topDomainsPreview = grouped.domains.slice(0, 3).map(d => d.domain).join(", ");

  return (
    <div className={`drSectionSources ${open ? "drSectionSources--open" : ""}`}>
      <button
        type="button"
        className="drSectionSourcesToggle"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
      >
        <i className="material-icons-outlined drSectionSourcesChevron">
          {open ? "expand_more" : "chevron_right"}
        </i>
        <span className="drSectionSourcesLabel">Sources</span>
        <span className="drSectionSourcesCount">{grouped.totalCount}</span>
        {!open && (
          <span className="drSectionSourcesPreview">· {topDomainsPreview}</span>
        )}
      </button>

      {open && (
        <div className="drSectionSourcesBody">
          {grouped.domains.map(({ domain, items }) => {
            const expanded = openDomains[domain] !== false; // default open inside parent
            return (
              <div key={domain} className="drSourceDomainGroup">
                <button
                  type="button"
                  className="drSourceDomainHeader"
                  onClick={() =>
                    setOpenDomains(s => ({ ...s, [domain]: !expanded }))
                  }
                  aria-expanded={expanded}
                >
                  <i className="material-icons-outlined drSourceDomainChevron">
                    {expanded ? "expand_more" : "chevron_right"}
                  </i>
                  <img
                    src={`https://www.google.com/s2/favicons?domain=${domain}&sz=32`}
                    alt=""
                    className="drSourceDomainFavicon"
                  />
                  <span className="drSourceDomainName">{domain}</span>
                  <span className="drSourceDomainCount">{items.length}</span>
                </button>
                {expanded && (
                  <ul className="drSourceDomainList">
                    {items.map((it, i) => (
                      <li key={i} className="drSourceDomainItem">
                        <a
                          href={it.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="drSourceDomainLink"
                          title={it.title || it.link}
                        >
                          {truncate(it.title || it.link, 110)}
                        </a>
                        {it.date && (
                          <span className="drSourceDomainDate">{it.date}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SectionSources;
